import { assign, clone, cloneDeep, each, find, isEmpty, isObject, filter, reduce, get, range, map } from "lodash";
import { ajaxCall } from "./../utils/misc";
import { setLocalStorage, getLocalStorage } from "./../utils/storage";

declare const jQuery: any;
const $ = jQuery;

const MIN_PWD_LEN = 8;
const ajaxAction: string = "iw_account_update_details";

type PasswordMatchFunc = () => boolean;
type PasswordStrongFunc = () => boolean;
type RequiredFilledFunc = () => boolean;

export function initAccountPasswordCheck(): void
{
	const $wraps = $(".account-signup form,.account-reset-password form,.account.update-details");
	if ($wraps.length === 0)
	{
		return;
	}
	$wraps.each(function () {
		const $wrap = $(this);
		const $passwordWrap = $wrap.find(`.input-wrap-password`);
		const $passwordConfirmWrap = $wrap.find(`.input-wrap-password-confirm`);
		const $password = $passwordWrap.find("input");
		const $passwordConfirm = $passwordConfirmWrap.find("input");
		const checkPasswordsMatch: PasswordMatchFunc = setupPasswordConfirm(
			$password, $passwordConfirm, $passwordConfirmWrap);
		const checkPasswordStrong: PasswordStrongFunc = setupPasswordStrength($password, $passwordWrap);
		const checkRequired: RequiredFilledFunc = setupRequiredFields($wrap);
		const $optInCheckbox = $wrap.find(`input[type="checkbox"][data-opt-in]`);
		const checkOptIn = (): boolean => {
			if ($optInCheckbox.length > 0 && !$optInCheckbox.prop("checked"))
			{
				alert("You must tick the opt-in box before proceeding!");
				return false;
			}
			return true;
		};
		const checkRecaptcha = (): boolean => {
			const $input = $wrap.find(`:input[name="g-recaptcha-response"]`);
			if ($input.length === 0 || $input.val())
			{
				return true;
			}
			alert("Please complete the Recaptcha!");
			return false;
		};
		restoreStashedFormData($wrap);
		$wrap.filter("form").on("submit", function (e) {
			if (!checkPasswordsMatch() ||
				!checkPasswordStrong() ||
				!checkRequired() ||
				!checkOptIn() ||
				!checkRecaptcha())
			{
				e.preventDefault();
				return false;
			}
			stashFormData($wrap);
		});
		$wrap.filter(".update-details").find(`[data-submit]`).click(function (e) {
			const $btn = $(this);
			if ($btn.is("[disabled]") ||
				!checkPasswordsMatch() ||
				!checkPasswordStrong() ||
				!checkRequired() ||
				!checkOptIn() ||
				!checkRecaptcha())
			{
				e.preventDefault();
				return false;
			}
			const data: any = {};
			$wrap.find(".input-wrap :input").each(function () {
				const $input = $(this);
				const name: string = $input.attr("name");
				let value: string|boolean = null;
				switch ($input.attr("type"))
				{
					case 'checkbox':
						value = $input.prop("checked");
						break;
					default:
						value = $input.val();
						break;
				}
				data[name] = value;
			});
			$wrap.addClass("busy");
			$btn.attr("disabled", "disabled");
			ajaxCall(ajaxAction, data).then((res) => {
				if (!res || !res.ok)
				{
					throw res;
				}
				$wrap.find(".success-msg").show();
			}).catch((reason) => {
				console.error("User account details ajax update failed:", reason);
				alert("Failed to update user account details."); //fixme better error handling
			}).then(() => {
				$wrap.removeClass("busy");
				$btn.removeAttr("disabled");
			});
		});
	});
}

function setupRequiredFields($form): RequiredFilledFunc
{
	const $inputs = $form.find(".input-wrap [required]");
	const $requiredWraps = $inputs.closest(".input-wrap");
	const check = (): boolean => {
		let ok: boolean = true;
		$requiredWraps.each(function () {
			const $wrap = $(this);
			const $error = $wrap.find(`[data-required-error]`);
			const $input = $wrap.find(`[required]`);
			const value: string = $.trim($input.val());
			if (!value)
			{
				ok = false;
				$error.show();
			}
			else
			{
				$error.hide();
			}
		});
		return ok;
	};
	return check;
}

function setupPasswordConfirm($password, $passwordConfirm, $passwordConfirmWrap): PasswordMatchFunc
{
	if ($password.length === 0 || $passwordConfirm.length === 0)
	{
		return () => true;
	}
	const $error = $passwordConfirmWrap.find(`[data-password-match-error]`);
	let confirmInputChanged: boolean = false;
	const check = (): boolean => {
		if ($password.val() === $passwordConfirm.val())
		{
			$error.hide();
			return true;
		}
		$error.show();
		return false;
	};
	$passwordConfirm.on("change", () => {
		confirmInputChanged = true;
		check();
	}).on("input", () => {
		if (confirmInputChanged)
		{
			check();
		}
	});
	$password.on("input", () => {
		if (confirmInputChanged)
		{
			check();
		}
	});
	return check;
}

function setupPasswordStrength($password, $passwordWrap): PasswordStrongFunc
{
	if ($password.length === 0)
	{
		return () => true;
	}
	const $error = $passwordWrap.find(`[data-weak-password-error]`);
	let inputChanged: boolean = false;
	const hasSpecialChar = (val: string): boolean => {
		return !!val.match(/\d/) && !!val.match(/[^a-z0-9]/i);
	};
	const check = (): boolean => {
		const val: string = $password.val();
		if (!$password.is(`[required]`) && !val)
		{
			$error.hide();
			return true;
		}
		if (val.length >= MIN_PWD_LEN && hasSpecialChar(val))
		{
			$error.hide();
			return true;
		}
		$error.show();
		return false;
	};
	$password.on("change", () => {
		inputChanged = true;
		check();
	}).on("input", () => {
		if (inputChanged)
		{
			check();
		}
	});
	return check;
}

function stashFormData($wrap): void
{
	const data: any = {};
	$wrap.find(`:input[name]:not([type="password"]):not([type="file"])`).each(function () {
		const $input = $(this);
		const name: string = $input.attr("name");
		if (name === "g-recaptcha-response")
		{
			return;
		}
		if ($input.is("[data-opt-in]"))
		{
			return;
		}
		data[name] = getInputValue($input);
	});
	setLocalStorage(createLocalStorageKey(), data, 600);
}

function getInputValue($input)
{
	const type: string = $input.attr("type");
	if (type === "checkbox" || type === "radio")
	{
		return $input.prop("checked") ? $input.val() : undefined;
	}
	return $input.val();
}

function restoreStashedFormData($wrap): void
{
	const data: any = getLocalStorage(createLocalStorageKey());
	if (!data)
	{
		return;
	}
	$wrap.find(`:input[name]:not([type="password"]):not([type="file"])`).each(function () {
		const $input = $(this);
		const name: string = $input.attr("name");
		const type: string = $input.attr("type");
		if (data[name] === undefined || data[name] === null)
		{
			return;
		}
		const value: any = data[name];
		if (type === "checkbox" || type === "radio")
		{
			if (value === $input.val())
			{
				$input.prop("checked", true).trigger("change");
			}
			return;
		}
		if (value)
		{
			$input.val(value).trigger("change");
		}
	});
}

function createLocalStorageKey(): string
{
	return "account_" + window.location.pathname;
}
